import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaUsers, FaTools, FaCalendarCheck, FaChartBar, FaSpinner } from 'react-icons/fa';

const AdminDashboard = () => {
    const [stats, setStats] = useState([
        { label: 'TOTAL USERS', value: '0', icon: <FaUsers />, color: '#0dcaf0' },
        { label: 'ACTIVE SERVICES', value: '0', icon: <FaTools />, color: '#ffc107' },
        { label: 'PENDING ORDERS', value: '0', icon: <FaCalendarCheck />, color: '#198754' },
        { label: 'TOTAL PROJECTS', value: '0', icon: <FaChartBar />, color: '#C5A059' },
    ]);
    const [recentBookings, setRecentBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const statusMapping = {
        '1': { label: 'Pending', class: 'pending', color: '#ffc107' },
        '2': { label: 'On Progress', class: 'progress', color: '#0dcaf0' },
        '3': { label: 'Complete', class: 'complete', color: '#198754' },
        '4': { label: 'Cancelled', class: 'cancelled', color: '#dc3545' }
    };

    const fetchDashboardData = async () => {
        setIsLoading(true);
        try {
            const [usersRes, servicesRes, bookingsRes] = await Promise.all([
                axios.get(`${BASE_URL}/users/getUser.php`),
                axios.get(`${BASE_URL}/service/getAllService.php`),
                axios.get(`${BASE_URL}/booking/getBooking.php`)
            ]);

            const usersCount = usersRes.data.status === 200 ? usersRes.data.data.length : 0;
            const servicesCount = servicesRes.data.status === 200 ? servicesRes.data.data.length : 0;
            const bookings = bookingsRes.data.status === 200 ? bookingsRes.data.data : [];

            // Count '1' for Pending bookings
            const newBookingsCount = bookings.filter(b => b.status === '1').length;

            setStats([
                { label: 'TOTAL USERS', value: usersCount.toLocaleString(), icon: <FaUsers />, color: '#0dcaf0' },
                { label: 'ACTIVE SERVICES', value: servicesCount.toLocaleString(), icon: <FaTools />, color: '#ffc107' },
                { label: 'PENDING ORDERS', value: newBookingsCount.toLocaleString(), icon: <FaCalendarCheck />, color: '#198754' },
                { label: 'TOTAL PROJECTS', value: bookings.length.toLocaleString(), icon: <FaChartBar />, color: '#C5A059' },
            ]);

            // Sort by latest first and take 5
            const sortedBookings = [...bookings].sort((a, b) =>
                (parseInt(b.booking_id || b.booked_id)) - (parseInt(a.booking_id || a.booked_id))
            ).slice(0, 5);
            setRecentBookings(sortedBookings);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-loading-container">
                <FaSpinner className="spinner" />
                <span>SYNCHRONIZING DATALINE...</span>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="mb-5 d-flex justify-content-between align-items-end">
                <div>
                    <span className="section-index">OVERVIEW</span>
                    <h2 className="display-6 text-white text-uppercase" style={{ letterSpacing: '3px' }}>Dashboard</h2>
                </div>
            </div>

            <div className="row g-4 mb-5">
                {stats.map((stat, index) => (
                    <div key={index} className="col-md-6 col-xl-3">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ color: stat.color }}>{stat.icon}</div>
                            <div className="stat-info">
                                <span className="stat-label">{stat.label}</span>
                                <h3 className="stat-value">{stat.value}</h3>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-4">
                <div className="col-lg-8">
                    <div className="dashboard-panel">
                        <h4 className="panel-title">RECENT PROJECT APPLICATIONS</h4>
                        <div className="table-wrapper mt-4">
                            <table className="admin-table-custom">
                                <thead>
                                    <tr>
                                        <th>PROJECT</th>
                                        <th>USER</th>
                                        <th>MOBILE</th>
                                        <th>STATUS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentBookings.map((booking) => (
                                        <tr key={booking.booking_id || booking.booked_id}>
                                            <td>{booking.serv_name}</td>
                                            <td>{booking.user_name}</td>
                                            <td>{booking.mobile_no}</td>
                                            <td>
                                                <span className={`status-badge ${statusMapping[booking.status]?.class || 'pending'}`}>
                                                    {(statusMapping[booking.status]?.label || 'Pending').toUpperCase()}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {recentBookings.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4 text-white-50">No recent bookings recorded.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="dashboard-panel">
                        <h4 className="panel-title">SYSTEM ALERTS</h4>
                        <div className="alert-list mt-4">
                            {recentBookings.filter(b => b.status === '1').length > 0 && (
                                <div className="alert-item">
                                    <div className="alert-indicator warning"></div>
                                    <div className="alert-text">
                                        {recentBookings.filter(b => b.status === '1').length} New applications pending review
                                    </div>
                                </div>
                            )}
                            <div className="alert-item">
                                <div className="alert-indicator info"></div>
                                <div className="alert-text">System Link: Stable (12 ms)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .admin-loading-container {
                    height: 60vh;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: var(--secondary-color);
                    gap: 20px;
                    letter-spacing: 4px;
                    font-size: 0.8rem;
                }

                .spinner {
                    font-size: 2.5rem;
                    animation: spin 2s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .stat-card {
                    background: rgba(255, 255, 255, 0.02);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    padding: 30px;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    position: relative;
                    overflow: hidden;
                }

                .stat-card::after {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%);
                    opacity: 0;
                    transition: opacity 0.4s ease;
                }

                .stat-card:hover {
                    background: rgba(255, 255, 255, 0.04);
                    border-color: rgba(197, 160, 89, 0.3);
                    transform: translateY(-5px);
                }
                
                .stat-card:hover::after {
                    opacity: 1;
                }

                .stat-icon {
                    font-size: 2rem;
                    opacity: 0.8;
                }

                .stat-label {
                    display: block;
                    font-size: 0.65rem;
                    letter-spacing: 2px;
                    color: rgba(255,255,255,0.4);
                    margin-bottom: 5px;
                }

                .stat-value {
                    color: white;
                    margin: 0;
                    font-weight: 300;
                    font-size: 1.5rem;
                }

                .dashboard-panel {
                    background: rgba(255, 255, 255, 0.015);
                    backdrop-filter: blur(15px);
                    border: 1px solid rgba(255, 255, 255, 0.03);
                    padding: 35px;
                    border-radius: 4px;
                    height: 100%;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                }

                .panel-title {
                    font-size: 0.75rem;
                    letter-spacing: 4px;
                    color: var(--secondary-color);
                    margin: 0;
                    font-weight: 400;
                    position: relative;
                    padding-bottom: 12px;
                }

                .panel-title::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    width: 40px;
                    height: 1px;
                    background: var(--secondary-color);
                    opacity: 0.5;
                }

                .admin-table-custom {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0 10px;
                    background-color: transparent !important;
                }

                .admin-table-custom th {
                    background: rgba(255,255,255,0.04) !important;
                    color: var(--secondary-color) !important;
                    font-size: 0.65rem;
                    letter-spacing: 2px;
                    font-weight: 600;
                    padding: 15px 20px;
                    text-transform: uppercase;
                    border: none !important;
                }

                .admin-table-custom tbody tr {
                    background: rgba(20, 20, 20, 0.6) !important;
                    transition: all 0.3s ease;
                    border-radius: 4px;
                }

                .admin-table-custom tbody tr:hover {
                    background: rgba(255,255,255,0.05) !important;
                    transform: translateX(5px);
                }

                .admin-table-custom td {
                    padding: 18px 20px;
                    font-size: 0.8rem;
                    vertical-align: middle;
                    color: #ffffff !important;
                    border: none !important;
                }

                .admin-table-custom td:first-child {
                    border-radius: 4px 0 0 4px;
                    font-weight: 500;
                }

                .admin-table-custom td:last-child {
                    border-radius: 0 4px 4px 0;
                }

                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 100px;
                    font-size: 0.6rem;
                    padding: 6px 12px;
                    border-radius: 2px;
                    letter-spacing: 1px;
                    font-weight: 700;
                    text-transform: uppercase;
                    white-space: nowrap;
                }

                .status-badge.pending { background: rgba(255, 193, 7, 0.1) !important; color: #ffc107 !important; border: 1px solid rgba(255, 193, 7, 0.4) !important; }
                .status-badge.progress { background: rgba(13, 202, 240, 0.1) !important; color: #0dcaf0 !important; border: 1px solid rgba(13, 202, 240, 0.4) !important; }
                .status-badge.complete { background: rgba(25, 135, 84, 0.1) !important; color: #198754 !important; border: 1px solid rgba(25, 135, 84, 0.4) !important; }
                .status-badge.cancelled { background: rgba(220, 53, 69, 0.1) !important; color: #dc3545 !important; border: 1px solid rgba(220, 53, 69, 0.4) !important; }

                .alert-item {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 15px;
                    background: rgba(255,255,255,0.01);
                    margin-bottom: 10px;
                    border-radius: 4px;
                    border: 1px solid rgba(255,255,255,0.03);
                }

                .alert-indicator {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .alert-indicator.warning { background: #ffc107; box-shadow: 0 0 10px rgba(255,193,7,0.5); }
                .alert-indicator.info { background: #0dcaf0; box-shadow: 0 0 10px rgba(13,202,240,0.5); }

                .alert-text {
                    font-size: 0.75rem;
                    color: white;
                    letter-spacing: 1px;
                }
            `}</style>
        </div>
    );
};

export default AdminDashboard;
